export function createFukushizaiDisplayRowMergeService(deps) {
    const {
        isEmpty,
        DicVal,
        canonicalPanelType,
        normalizeGroupNo,
        readRowValueIgnoreCase,
        normalizeFixedVerticalDisplayRows,
        readFukushizaiDisplayBaseRowId,
        readFukushizaiDisplayExplicitBaseRowId,
        readFukushizaiDisplaySourceColumnId,
        debugFukushizaiSyncLog
    } = deps;

    // グリッドイベント行を既存の表示行へ安全にマージします。
    function mergeFukushizaiEventRowIntoRows(panelInfo, rows, eventRow) {
        const list = Array.isArray(rows) ? rows : [];
        if (isEmpty(eventRow)) return list;

        const restoredEventRow = restoreFukushizaiDisplayMetaFromCurrentRows(panelInfo, eventRow, list);
        const eventBaseRowId = readFukushizaiDisplayExplicitBaseRowId(restoredEventRow);
        const eventSourceColumnId = readFukushizaiDisplaySourceColumnId(restoredEventRow);
        const eventRowId = String(DicVal(restoredEventRow, "rowid", "") ?? "").trim();
        let replaced = false;

        const mergedRows = list.map((row) => {
            if (isEmpty(row)) return row;
            const sameSource = eventSourceColumnId !== "" && readFukushizaiDisplaySourceColumnId(row) === eventSourceColumnId;
            const sameBase = eventBaseRowId !== "" && readFukushizaiDisplayExplicitBaseRowId(row) === eventBaseRowId;
            const sameRow = eventRowId !== "" && String(DicVal(row, "rowid", "") ?? "").trim() === eventRowId;

            // source/base の組み合わせか rowid が一致した行だけ置き換えます。
            if ((sameSource && sameBase) || sameRow) {
                replaced = true;
                return restoreFukushizaiDisplayMetaFromCurrentRows(panelInfo, restoredEventRow, [row]);
            }
            return row;
        });

        // 一致行がなければ新規行として末尾へ追加します。
        if (!replaced) mergedRows.push(restoredEventRow);
        return normalizeFixedVerticalDisplayRows(panelInfo, mergedRows);
    }

    // 既存表示行に保持していた表示専用メタをイベント行へ戻します。
    function restoreFukushizaiDisplayMetaFromCurrentRows(panelInfo, eventRow, currentRows) {
        if (isEmpty(eventRow)) return eventRow;

        const list = Array.isArray(currentRows) ? currentRows : [];
        const displayRowId = String(DicVal(eventRow, "rowid", "") ?? "").trim();
        const sourceColumnId = readFukushizaiDisplaySourceColumnId(eventRow);
        const current = list.find((row) => {
            if (isEmpty(row)) return false;
            const rowId = String(DicVal(row, "rowid", "") ?? "").trim();

            // rowid が一致する場合は同一表示行として扱います。
            if (displayRowId !== "" && rowId === displayRowId) return true;

            // rowid が使えない場合は source column id で同一表示行を特定します。
            return sourceColumnId !== "" && readFukushizaiDisplaySourceColumnId(row) === sourceColumnId;
        });
        if (isEmpty(current)) return eventRow;

        const restored = { ...eventRow };
        [
            "__fukushizaiBaseRowId",
            "__fukushizaiSourceColumnId",
            "__komonofudaBaseRowId",
            "__komonofudaIndex",
            "__komonofudaSourceColumnId",
            "__komonofudaLabel",
            "__komonofudaCode",
            "__komonofudaName",
            "__brandNameBaseRowId",
            "__brandNameSourceColumnId",
            "__otherNameBaseRowId",
            "__otherNameSourceColumnId",
            "__otherFukuzaiBaseRowId",
            "__otherFukuzaiSourceColumnId"
        ].forEach((key) => {
            // イベント行に値がない項目だけ、現在行から復元します。
            if (isEmpty(DicVal(restored, key, "")) && !isEmpty(DicVal(current, key, ""))) {
                restored[key] = current[key];
            }
        });

        debugFukushizaiSyncLog("[Kakousiji][fukushizai event row meta restored]", {
            type: canonicalPanelType(DicVal(panelInfo, "type", "")),
            rowid: displayRowId,
            baseRowId: readFukushizaiDisplayExplicitBaseRowId(restored),
            sourceColumnId: readFukushizaiDisplaySourceColumnId(restored)
        });
        return restored;
    }

    // AG Grid のイベント引数から実際に使う行データを組み立てます。
    function readFukushizaiEventRow(params) {
        if (isEmpty(params)) return null;
        const data = DicVal(params, "data", null);
        const rowNodeData = DicVal(DicVal(params, "rowNode", null), "data", null);

        // 変更後 data と rowNode.data の両方がある場合は両者をマージします。
        if (!isEmpty(data) && !isEmpty(rowNodeData)) return { ...rowNodeData, ...data };
        if (!isEmpty(rowNodeData)) return rowNodeData;
        if (!isEmpty(data)) return data;
        return null;
    }

    // 副資材明細の行照合で使うキーを正規化します。
    function normalizeFukushizaiDetailMatchInfo(matchInfo) {
        const source = isEmpty(matchInfo) ? {} : matchInfo;
        return {
            rowid: String(DicVal(source, "rowid", "") ?? "").trim(),
            baseRowId: readFukushizaiDisplayBaseRowId(source),
            sourceColumnId: readFukushizaiDisplaySourceColumnId(source),
            itemCd: String(DicVal(source, "SSDZ_ITMCITEMCD", DicVal(source, "itemCd", "")) ?? "").trim(),
            colorCd: String(DicVal(source, "SSDZ_COLORCD", DicVal(source, "colorCd", "")) ?? "").trim(),
            groupNo: normalizeGroupNo(DicVal(source, "SSDZ_FUKUSIZAICOLORGRP", DicVal(source, "groupNo", ""))),
            ssdzNo: String(DicVal(source, "SSDZ_NO", "") ?? "").trim()
        };
    }

    // base row id がなくても item/color/group/no の一致で候補行を探します。
    function findFukushizaiDetailRowIndexByMatchInfo(rows, matchInfo) {
        const list = Array.isArray(rows) ? rows : [];
        const match = normalizeFukushizaiDetailMatchInfo(matchInfo);
        if (list.length === 0) return -1;

        return list.findIndex((row) => {
            if (isEmpty(row)) return false;

            const rowId = String(DicVal(row, "rowid", "") ?? "").trim();
            if (match.baseRowId !== "" && rowId === match.baseRowId) return true;
            if (match.baseRowId === "" && match.rowid !== "" && rowId === match.rowid) return true;

            const itemCd = String(readRowValueIgnoreCase(row, "SSDZ_ITMCITEMCD") ?? "").trim();
            const colorCd = String(readRowValueIgnoreCase(row, "SSDZ_COLORCD") ?? "").trim();
            const groupNo = normalizeGroupNo(readRowValueIgnoreCase(row, "SSDZ_FUKUSIZAICOLORGRP"));
            const ssdzNo = String(readRowValueIgnoreCase(row, "SSDZ_NO") ?? "").trim();

            // 明細キーのどれかが不一致なら別行として扱います。
            if (match.itemCd !== "" && itemCd !== match.itemCd) return false;
            if (match.colorCd !== "" && colorCd !== match.colorCd) return false;
            if (match.groupNo !== "" && groupNo !== match.groupNo) return false;
            if (match.ssdzNo !== "" && ssdzNo !== match.ssdzNo) return false;

            // 1 つ以上の照合キーが入っているときだけ候補行として採用します。
            return match.itemCd !== "" || match.colorCd !== "" || match.groupNo !== "" || match.ssdzNo !== "";
        });
    }

    // rowid / baseRowId が一致する厳密な行だけを探します。
    function findFukushizaiDetailExactRowIndexByMatchInfo(rows, matchInfo) {
        const list = Array.isArray(rows) ? rows : [];
        const match = normalizeFukushizaiDetailMatchInfo(matchInfo);
        if (list.length === 0) return -1;

        // 厳密一致では rowid 系がないと照合できないため未検出にします。
        if (match.baseRowId === "" && match.rowid === "") return -1;

        return list.findIndex((row) => {
            if (isEmpty(row)) return false;
            const rowId = String(DicVal(row, "rowid", "") ?? "").trim();
            if (match.baseRowId !== "") return rowId === match.baseRowId;
            return match.rowid !== "" && rowId === match.rowid;
        });
    }

    return {
        mergeFukushizaiEventRowIntoRows,
        restoreFukushizaiDisplayMetaFromCurrentRows,
        readFukushizaiEventRow,
        normalizeFukushizaiDetailMatchInfo,
        findFukushizaiDetailRowIndexByMatchInfo,
        findFukushizaiDetailExactRowIndexByMatchInfo
    };
}
